import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { UserProfile, UserRole } from "@/lib/supabase";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { UserPlus, RefreshCw } from "lucide-react";
import UserSearchBar from "./UserSearchBar";
import UsersTable from "./UsersTable";
import AuditLogCard from "./AuditLogCard";
import { logInfo, logError, logWarn } from "@/utils/logger";

const UserManagement = () => {
  const { toast } = useToast();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  
  logInfo("UserManagement component initialized", "UserManagement");
  
  useEffect(() => {
    // Log component mount
    logInfo("UserManagement component mounted", "UserManagement");
    
    // Initial users fetch
    fetchUsers();
    
    return () => {
      logInfo("UserManagement component unmounted", "UserManagement");
    };
  }, []);

  const fetchUsers = async () => {
    logInfo("Fetching users", "UserManagement", { 
      currentUserCount: users.length,
      searchTerm
    });
    
    setIsLoading(true);
    try {
      logInfo("Making API call to fetch users", "UserManagement");
      const { data, error } = await supabase
        .from('profiles')
        .select()
        .order('created_at', { ascending: false });

      if (error) {
        logError("Error fetching users", "UserManagement", {
          error: error.message,
          code: error.code,
          details: error.details
        });
        throw error;
      }
      
      logInfo("Users fetched successfully", "UserManagement", { 
        userCount: data?.length || 0,
        firstUserId: data?.[0]?.id
      });
      
      // TypeCast the data to UserProfile[] to ensure the role property is treated as UserRole
      const typedData = data?.map(user => ({
        ...user,
        role: user.role as UserRole
      })) || [];
      
      setUsers(typedData);
    } catch (error) {
      logError("Exception fetching users", "UserManagement", {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined
      });
      
      toast({
        title: "Error",
        description: "Failed to fetch users",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      logInfo("User fetch completed", "UserManagement", {
        success: users.length > 0,
        userCount: users.length
      });
    }
  };

  const updateUserRole = async (userId: string, newRole: UserRole) => {
    logInfo("Updating user role", "UserManagement", { 
      userId, 
      newRole,
      previousRole: users.find(u => u.id === userId)?.role
    });
    
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ role: newRole })
        .eq("id", userId);

      if (error) {
        logError("Error updating user role", "UserManagement", {
          userId,
          newRole,
          error: error.message,
          code: error.code
        });
        throw error;
      }

      logInfo("User role updated successfully", "UserManagement", {
        userId,
        newRole
      });
      
      toast({
        title: "Success",
        description: "User role updated successfully",
      });

      setUsers(
        users.map((user) =>
          user.id === userId ? { ...user, role: newRole } : user
        )
      );
    } catch (error) {
      logError("Exception updating user role", "UserManagement", {
        userId,
        newRole,
        error: error instanceof Error ? error.message : String(error)
      });
      
      toast({
        title: "Error",
        description: "Failed to update user role",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    if (searchTerm) {
      logInfo("Search term changed", "UserManagement", { searchTerm });
    }
  }, [searchTerm]);

  const filteredUsers = users.filter(
    (user) =>
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  logInfo("Rendering UserManagement component", "UserManagement", {
    totalUsers: users.length,
    filteredUserCount: filteredUsers.length,
    isLoading,
    hasSearchTerm: !!searchTerm
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>User Management</CardTitle>
          <CardDescription>
            Manage user accounts and permissions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between mb-4">
            <UserSearchBar 
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
            />
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={fetchUsers}
                disabled={isLoading}
              >
                {isLoading ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Refresh
              </Button>
              <Button>
                <UserPlus className="h-4 w-4 mr-2" />
                Invite User
              </Button>
            </div>
          </div>

          <UsersTable 
            users={users}
            filteredUsers={filteredUsers}
            isLoading={isLoading}
            updateUserRole={updateUserRole}
          />
        </CardContent>
      </Card>
      
      <div className="mt-6">
        <Card>
          <CardHeader>
            <CardTitle>Audit Log</CardTitle>
            <CardDescription>Recent system activity</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground text-center py-6">
              Select a user to view their audit log
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UserManagement;
